import { useMemo } from "react";
import { useParams } from "wouter";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader } from "@/components/layout/page-header";
import { DataCard } from "@/components/data/data-card";
import { EmptyState } from "@/components/data/empty-state";
import { PageSkeleton } from "@/components/data/page-skeleton";
import {
  useFuncionarios, useFechamentos, useAdicionaisPorCompetencia,
  useAuxiliosPorCompetencia, useTarefasByPeriodo,
} from "@/hooks/use-equipe";
import { useSimulacaoRemuneracao, parseErroRemuneracao } from "@/hooks/use-remuneracao";
import {
  paraCentavos, intervaloDaCompetencia, ehMensalista, formatarDataCurta, formatarBRL,
} from "@shared/equipe-fechamento";
import { presencaDoMes, calcCustoPorDia, calcBrutoFichaCentavos, calcLiquidoFichaCentavos } from "@shared/ficha-calculo";
import { competenciaLabel, nota2, DestaqueNota } from "@/components/equipe/nota-mensal";

/**
 * `/equipe/ficha/:funcionarioId?competencia=AAAA-MM` — a resposta única para
 * "quanto essa pessoa recebe este mês, e por quê" (Prompt_Code_Financeiro_Ficha.md).
 *
 * Só LEITURA — não grava nada, editar continua onde já é hoje (Financeiro,
 * fechamento mensal). Não recalcula nada: fixo/variável vêm de `getSimulacao`
 * (Remuneração), adiantamentos/descontos vêm de `fechamento_mensal`, presença
 * vem de `tarefas`. `shared/ficha-calculo.ts` só SOMA o que essas fontes já
 * calcularam — a primeira vez que a conta inteira aparece num lugar só.
 */

// A competência descreve O QUE a ficha mostra, então mora na URL: reload, link
// no WhatsApp e "voltar" do celular caem no mesmo mês. Vazia = mês corrente —
// derivar em vez de guardar mantém `/equipe/ficha/:id` limpo na barra.
// ⚠️ Constante de MÓDULO (dep de useMemo dentro do hook).
const PADRAO_FICHA = { competencia: "" };

function formatarBRLCentavos(centavos: number): string {
  return (centavos / 100).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

export default function EquipeFicha() {
  const { funcionarioId } = useParams<{ funcionarioId: string }>();
  const { estado, definir } = useEstadoNaUrl(PADRAO_FICHA);
  const { voltar } = useNavegacao();
  const competencia = estado.competencia || new Date().toISOString().slice(0, 7);

  const { data: funcionarios, isLoading: loadingFunc } = useFuncionarios(true);
  const funcionario = funcionarios?.find(f => f.id === funcionarioId);

  const { data: simulacao, isLoading: loadingSim, isError: erroSim, error: erroSimRaw } =
    useSimulacaoRemuneracao(competencia);
  const pessoaSim = simulacao?.porPessoa.find(p => p.funcionarioId === funcionarioId);
  const erroSimulacao = erroSim ? parseErroRemuneracao(erroSimRaw) : null;

  const { data: fechamentos, isLoading: loadingFech } = useFechamentos(competencia);
  const fechamento = fechamentos?.find(f => f.funcionarioId === funcionarioId);

  const { data: adicionaisComp } = useAdicionaisPorCompetencia(competencia);
  const adicionalPessoa = adicionaisComp?.find(a => a.funcionario_id === funcionarioId);

  const { data: auxiliosComp } = useAuxiliosPorCompetencia(competencia);
  const auxilioPessoa = auxiliosComp?.find(a => a.funcionario_id === funcionarioId);

  const { inicio, fim } = intervaloDaCompetencia(competencia);
  const { data: tarefas, isLoading: loadingTarefas } = useTarefasByPeriodo(inicio, fim);
  const presenca = useMemo(
    () => presencaDoMes(tarefas ?? [], funcionarioId ?? ""),
    [tarefas, funcionarioId],
  );

  const isLoading = loadingFunc || loadingSim || loadingFech || loadingTarefas;

  const mensalista = funcionario ? ehMensalista(funcionario) : false;

  // Fixo/variável: SEMPRE a fórmula (getSimulacao), quando existir — é o que
  // este teste verifica ao centavo. Sem vínculo de setor não há fórmula
  // possível: cai no salário cadastrado (única base que existe).
  const fixoCentavos = pessoaSim ? pessoaSim.fixoCentavos : paraCentavos(funcionario?.salarioMensal);
  const variavelCentavos = pessoaSim ? pessoaSim.variavelCentavos : 0;
  const auxiliosCentavos = paraCentavos(auxilioPessoa?.total);
  const adicionaisCentavos = paraCentavos(adicionalPessoa?.total);
  const bruto = calcBrutoFichaCentavos({ fixoCentavos, variavelCentavos, auxiliosCentavos, adicionaisCentavos });

  const temFechamento = !!fechamento;
  const liquido = temFechamento
    ? calcLiquidoFichaCentavos({
        brutoCentavos: bruto, adiantamentosPagos: fechamento.adiantamentosPagos, descontos: fechamento.descontos,
      })
    : null;

  const cadastradoCentavos = temFechamento ? paraCentavos(fechamento.salarioFixo) : paraCentavos(funcionario?.salarioMensal);
  const mostrarComparacao = mensalista && (funcionario?.salarioModo ?? "FIXO") !== "DERIVADO" && !!pessoaSim;

  const custoPorDia = calcCustoPorDia(fixoCentavos, presenca.diasTrabalhados);

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 pb-24">
        <PageSkeleton variante="cards" />
      </div>
    );
  }

  if (!funcionario) {
    return (
      <div className="p-4 sm:p-6 space-y-4 pb-24">
        <PageHeader titulo="Ficha do mês" />
        <EmptyState titulo="Funcionário não encontrado" descricao="Confira o link — ele pode ter sido apagado." />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 pb-24">
      <div className="flex items-center gap-2">
        {/* Voltar de verdade: a ficha se abre do Financeiro (com aba e competência),
            de Funcionários e da Auditoria — um href fixo mandava todo mundo para o
            Financeiro, e ainda por cima sem a aba. O fallback só vale para quem
            chegou por link direto. */}
        <Button
          variant="ghost"
          size="icon"
          aria-label="Voltar"
          onClick={() => voltar("/equipe/financeiro?aba=mes")}
          data-testid="button-voltar-ficha"
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <PageHeader
          titulo={`${funcionario.apelido ?? funcionario.nome} · ${competenciaLabel(competencia)}`}
          descricao="Quanto essa pessoa recebe este mês, e por quê — só leitura"
        />
      </div>

      <div className="space-y-1 max-w-[10rem]">
        <Label className="text-xs">Competência</Label>
        <Input type="month" className="h-11" value={competencia} onChange={e => definir({ competencia: e.target.value })} />
      </div>

      {!mensalista ? (
        <DiaristaFicha
          adicionaisCentavos={adicionaisCentavos} adicionalQtd={adicionalPessoa?.qtd ?? 0}
          auxiliosCentavos={auxiliosCentavos} auxilioQtd={auxilioPessoa?.qtd ?? 0}
          presenca={presenca}
        />
      ) : (
        <>
          {pessoaSim ? (
            <DataCard titulo="Nota do mês" padded>
              <DestaqueNota pessoa={pessoaSim} />
              {pessoaSim.setores.length > 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  {pessoaSim.setores.map(s => `${s.setorNome ?? "?"} ${nota2(s.nota)}${s.foraDaMedia ? " (fora)" : ""}`).join(" · ")}
                </p>
              )}
            </DataCard>
          ) : erroSimulacao ? (
            <DataCard titulo="Nota do mês" padded>
              <p className="text-sm text-muted-foreground">{erroSimulacao.message}</p>
            </DataCard>
          ) : (
            <DataCard titulo="Nota do mês" padded>
              <p className="text-sm text-muted-foreground">Sem vínculo de setor este mês — não há fórmula por hectare para esta pessoa.</p>
            </DataCard>
          )}

          <DataCard titulo="Remuneração" padded>
            <div className="space-y-2 text-sm">
              {pessoaSim && (
                <p className="text-xs text-muted-foreground">
                  {pessoaSim.haDaPessoa.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} ha × {formatarBRLCentavos(paraCentavos(simulacao?.taxa.valorFixoHa))}/ha
                  {" × "}{pessoaSim.percentualFixo ?? 0}%
                </p>
              )}
              <LinhaRemuneracao label="fixo" valor={formatarBRLCentavos(fixoCentavos)} />
              {pessoaSim && (
                <p className="text-xs text-muted-foreground -mt-1">
                  {pessoaSim.haDaPessoa.toLocaleString("pt-BR", { minimumFractionDigits: 2 })} ha × {formatarBRLCentavos(paraCentavos(simulacao?.taxa.valorVariavelHa))}/ha
                  {" × "}{pessoaSim.percentualVariavel ?? 0}%
                </p>
              )}
              <LinhaRemuneracao label="variável" valor={formatarBRLCentavos(variavelCentavos)} />
              <LinhaRemuneracao label="auxílios" valor={formatarBRLCentavos(auxiliosCentavos)} sub={auxilioPessoa ? `${auxilioPessoa.qtd} lançamento${auxilioPessoa.qtd === 1 ? "" : "s"}` : undefined} />
              <LinhaRemuneracao label="adicionais" valor={formatarBRLCentavos(adicionaisCentavos)} sub={adicionalPessoa ? `${adicionalPessoa.qtd} lançamento${adicionalPessoa.qtd === 1 ? "" : "s"}` : "lançamentos do mês"} />

              {mostrarComparacao && (
                <p className="text-[11px] text-muted-foreground pt-1 border-t">
                  Cadastrado (é o que está valendo): {formatarBRLCentavos(cadastradoCentavos)} · pela fórmula (nota): {formatarBRLCentavos(pessoaSim!.fixoCentavos)}
                </p>
              )}

              <div className="border-t pt-2 flex items-center justify-between font-semibold">
                <span>bruto</span><span>{formatarBRLCentavos(bruto)}</span>
              </div>

              {temFechamento ? (
                <>
                  <LinhaRemuneracao label="− adiantamentos pagos" valor={`− ${formatarBRL(fechamento!.adiantamentosPagos)}`} />
                  <LinhaRemuneracao label="− descontos" valor={`− ${formatarBRL(fechamento!.descontos)}`} />
                  <div className="border-t-2 pt-2 flex items-center justify-between font-bold text-base">
                    <span>LÍQUIDO</span><span>{formatarBRLCentavos(liquido ?? 0)}</span>
                  </div>
                </>
              ) : (
                <p className="text-xs text-muted-foreground pt-1">
                  Adiantamentos e descontos — fechamento ainda não gerado para este mês. Líquido: —
                </p>
              )}
            </div>
          </DataCard>

          <PresencaCard presenca={presenca} custoPorDia={custoPorDia} />
        </>
      )}
    </div>
  );
}

function LinhaRemuneracao({ label, valor, sub }: { label: string; valor: string; sub?: string }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">
        {label}{sub && <span className="text-[11px]"> · {sub}</span>}
      </span>
      <span className="font-medium tabular-nums">{valor}</span>
    </div>
  );
}

function PresencaCard({ presenca, custoPorDia }: {
  presenca: { diasTrabalhados: number; diasFalta: number; datasFalta: string[] };
  custoPorDia: string | null;
}) {
  return (
    <DataCard titulo="Presença" padded>
      <p className="text-sm">
        {presenca.diasTrabalhados} dia{presenca.diasTrabalhados === 1 ? "" : "s"} trabalhado{presenca.diasTrabalhados === 1 ? "" : "s"}
        {presenca.diasFalta > 0 && (
          <> · {presenca.diasFalta} falta{presenca.diasFalta === 1 ? "" : "s"} ({presenca.datasFalta.map(d => formatarDataCurta(d)).join(", ")})</>
        )}
      </p>
      {custoPorDia !== null && (
        <p className="text-xs text-muted-foreground mt-1">custo por dia trabalhado: {formatarBRL(custoPorDia)}</p>
      )}
    </DataCard>
  );
}

/** Diarista: sem fórmula por hectare (isso é de encarregado) — presença + o que já existe de lançamentos. */
function DiaristaFicha({ adicionaisCentavos, adicionalQtd, auxiliosCentavos, auxilioQtd, presenca }: {
  adicionaisCentavos: number; adicionalQtd: number;
  auxiliosCentavos: number; auxilioQtd: number;
  presenca: { diasTrabalhados: number; diasFalta: number; datasFalta: string[] };
}) {
  return (
    <>
      <DataCard titulo="Diarista" padded aviso="A remuneração por diária é por quinzena — veja o valor pago em Financeiro. Aqui, o que já é mensal: presença e lançamentos.">
        <div className="space-y-2 text-sm">
          <LinhaRemuneracao label="auxílios" valor={formatarBRLCentavos(auxiliosCentavos)} sub={auxilioQtd > 0 ? `${auxilioQtd} lançamento${auxilioQtd === 1 ? "" : "s"}` : undefined} />
          <LinhaRemuneracao label="adicionais" valor={formatarBRLCentavos(adicionaisCentavos)} sub={adicionalQtd > 0 ? `${adicionalQtd} lançamento${adicionalQtd === 1 ? "" : "s"}` : "lançamentos do mês"} />
        </div>
      </DataCard>
      <PresencaCard presenca={presenca} custoPorDia={null} />
    </>
  );
}
